// File Location: src/component/policy/ConsultationScheduler.js

import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Phone, 
  Video, 
  MapPin, 
  FileText, 
  Download,
  Mic, 
  Upload,
  Save,
  X,
  CheckCircle,
  AlertTriangle,
  Star,
  MessageSquare,
  Users
} from 'lucide-react';
import {
  schedulePatientConsultation,
  completePatientConsultation,
  getApplicationConsultations,
  getConsultationScripts,
  rescheduleConsultation,
  cancelConsultation
} from '../../services/policyWriterService';
import { useAuth } from '../../context/AuthContext';

const ConsultationScheduler = ({ application, onConsultationUpdate }) => {
  const { userData: user } = useAuth();
  const [activeTab, setActiveTab] = useState('schedule');
  const [consultations, setConsultations] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [scriptType, setScriptType] = useState('kyp-initial-counselling');
  const [loading, setLoading] = useState(false);

  // Schedule form state
  const [scheduleData, setScheduleData] = useState({
    type: 'kyp-initial-counselling',
    scheduledDate: '',
    scheduledTime: '',
    contactMethod: 'phone',
    patientPhone: application?.patient?.phone || '',
    patientEmail: application?.patient?.email || '',
    agenda: [],
    notes: '',
    priority: 'normal'
  });

  // Completion form state
  const [completionData, setCompletionData] = useState({
    duration: 0,
    topicsDiscussed: [],
    consultationNotes: '',
    patientFeedback: '',
    actionItems: [],
    followUpRequired: false,
    followUpDate: '',
    patientSatisfaction: 5,
    issues: [],
    resolutions: [],
    audioFile: null
  });

  const [newTopic, setNewTopic] = useState('');
  const [newActionItem, setNewActionItem] = useState('');
  const [newIssue, setNewIssue] = useState('');
  const [newResolution, setNewResolution] = useState('');

  useEffect(() => {
    loadConsultations();
    
    // Reset consultation type if current selection is not allowed by gating rules
    const mv = application?.workflow?.medicalVerification || {};
    const ack = mv?.kypHospitalAcknowledgement?.status || mv?.kypAck || mv?.kypStatus;
    const kypAccepted = ack === 'accepted';
    const ds = application?.workflow?.dischargeSettlement || {};
    const dispatchObj = application?.workflow?.dispatch || application?.workflow?.dispatches || {};
    const dispatchApproved = (
      application?.workflow?.dispatchClearance?.status === 'approved' ||
      dispatchObj?.status === 'completed' ||
      ds?.dispatchClearance?.status === 'approved' ||
      ds?.dispatchReview?.status === 'approved'
    );

    if (scheduleData.type === 'discharge-counselling' && !dispatchApproved) {
      setScheduleData(prev => ({ ...prev, type: 'kyp-initial-counselling' }));
    }
    if (scheduleData.type === 'kyp-initial-counselling' && !kypAccepted) {
      setScheduleData(prev => ({ ...prev, type: 'kyp-initial-counselling' }));
    }
  }, [application]);

  const loadConsultations = async () => {
    if (!application?.applicationId) return;
    
    try {
      const result = await getApplicationConsultations(application.applicationId);
      if (result.success) {
        setConsultations(result.consultations);
      }
    } catch (error) {
      console.error('Error loading consultations:', error);
    }
  };

  const handleScheduleConsultation = async () => {
    if (!scheduleData.scheduledDate || !scheduleData.scheduledTime) {
      alert('Please select date and time for consultation');
      return;
    }

    // Gate Session 1 (KYP Initial) behind KYP acceptance
    const mv = application?.workflow?.medicalVerification || {};
    const ack = mv?.kypHospitalAcknowledgement?.status || mv?.kypAck || mv?.kypStatus;
    const kypAccepted = ack === 'accepted';
    if (scheduleData.type === 'kyp-initial-counselling' && !kypAccepted) {
      alert('KYP Initial Counselling is available only after KYP is accepted by hospital');
      return;
    }

    // Gate Session 2 (Discharge) behind dispatch approval
    const ds = application?.workflow?.dischargeSettlement || {};
    const dispatchObj = application?.workflow?.dispatch || application?.workflow?.dispatches || {};
    const dispatchApproved = (
      application?.workflow?.dispatchClearance?.status === 'approved' ||
      dispatchObj?.status === 'completed' ||
      ds?.dispatchClearance?.status === 'approved' ||
      ds?.dispatchReview?.status === 'approved'
    );
    if (scheduleData.type === 'discharge-counselling' && !dispatchApproved) {
      alert('Discharge Counselling is available only after dispatch process is approved');
      return;
    }

    setLoading(true);
    try {
      const result = await schedulePatientConsultation(
        application.applicationId,
        user.uid,
        scheduleData
      );

      if (result.success) {
        alert('Consultation scheduled successfully!');
        setShowScheduleForm(false);
        setScheduleData({
          type: 'kyp-initial-counselling',
          scheduledDate: '',
          scheduledTime: '',
          contactMethod: 'phone',
          patientPhone: application?.patient?.phone || '',
          patientEmail: application?.patient?.email || '',
          agenda: [],
          notes: '',
          priority: 'normal'
        });
        loadConsultations();
        onConsultationUpdate?.();
      } else {
        alert('Error scheduling consultation: ' + result.error);
      }
    } catch (error) {
      console.error('Error scheduling consultation:', error);
      alert('Error scheduling consultation');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteConsultation = async () => {
    if (!completionData.consultationNotes.trim()) {
      alert('Please add consultation notes');
      return;
    }

    setLoading(true);
    try {
      const result = await completePatientConsultation(selectedConsultation.id, {
        ...completionData,
        completedBy: user.uid
      });

      if (result.success) {
        alert('Consultation completed successfully!');
        setShowCompleteForm(false);
        setSelectedConsultation(null);
        setCompletionData({
          duration: 0,
          topicsDiscussed: [],
          consultationNotes: '',
          patientFeedback: '',
          actionItems: [],
          followUpRequired: false,
          followUpDate: '',
          patientSatisfaction: 5,
          issues: [],
          resolutions: [],
          audioFile: null
        });
        loadConsultations();
        onConsultationUpdate?.();
      } else {
        alert('Error completing consultation: ' + result.error);
      }
    } catch (error) {
      console.error('Error completing consultation:', error);
      alert('Error completing consultation');
    } finally {
      setLoading(false);
    }
  };

  const addToList = (list, item, setter) => {
    if (item.trim()) {
      setter(prev => ({
        ...prev,
        [list]: [...prev[list], item.trim()]
      }));
    }
  };

  const removeFromList = (list, index, setter) => {
    setter(prev => ({
      ...prev,
      [list]: prev[list].filter((_, i) => i !== index)
    }));
  };

  const getConsultationStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helpers for gating and labels
  const isKypAccepted = () => {
    const mv = application?.workflow?.medicalVerification || {};
    const ack = mv?.kypHospitalAcknowledgement?.status || mv?.kypAck || mv?.kypStatus;
    return ack === 'accepted';
  };

  const isDispatchApproved = () => {
    const ds = application?.workflow?.dischargeSettlement || {};
    const dispatchObj = application?.workflow?.dispatch || application?.workflow?.dispatches || {};
    return (
      application?.workflow?.dispatchClearance?.status === 'approved' ||
      dispatchObj?.status === 'completed' ||
      ds?.dispatchClearance?.status === 'approved' ||
      ds?.dispatchReview?.status === 'approved'
    );
  };

  const isTypeAllowed = (type) => {
    if (type === 'kyp-initial-counselling') return isKypAccepted();
    if (type === 'discharge-counselling') return isDispatchApproved();
    return true;
  };

  // Determine available counselling types for scheduling dropdown
  const getAvailableConsultationTypes = () => {
    const pv = application?.workflow?.policyVerification || {};
    const kypStatus = (pv?.kypInitialCounselling?.status || '').toLowerCase();
    const dischargeStatus = (pv?.dischargeCounselling?.status || '').toLowerCase();

    const available = [];
    // Show KYP only if gated and not completed
    if (isKypAccepted() && kypStatus !== 'completed') {
      available.push('kyp-initial-counselling');
    }
    // Show Discharge only if gated and not completed
    if (isDispatchApproved() && dischargeStatus !== 'completed') {
      available.push('discharge-counselling');
    }
    return available;
  };

  // Keep schedule type consistent with availability
  useEffect(() => {
    const available = getAvailableConsultationTypes();
    if (available.length > 0 && !available.includes(scheduleData.type)) {
      setScheduleData(prev => ({ ...prev, type: available[0] }));
    }
  }, [application, showScheduleForm]);

  // Scrutiny/Dispatch documents helpers
  const getScrutinyChecklist = () => {
    return application?.workflow?.dischargeSettlement?.scrutinyChecklist || [];
  };

  const getApprovedDocsSummary = () => {
    const checklist = getScrutinyChecklist();
    if (!Array.isArray(checklist) || checklist.length === 0) return null;
    const approved = checklist.filter(i => (i?.status || '').toLowerCase() === 'approved').length;
    const total = checklist.length;
    return { approved, total };
  };

  const collectScrutinyDocUrls = () => {
    const urls = [];
    try {
      // 1) From scrutiny checklist
      const checklist = getScrutinyChecklist();
      if (Array.isArray(checklist)) {
        checklist.forEach((i) => {
          const u = i?.url || i?.fileUrl || i?.downloadUrl;
          if (u) urls.push({ url: u, name: i?.fileName || i?.title || i?.name || 'document' });
        });
      }
      // 2) From dischargeSettlement.documents or scrutinySubmission.documents
      const ds = application?.workflow?.dischargeSettlement || {};
      const dsDocs = Array.isArray(ds?.documents) ? ds.documents : [];
      dsDocs.forEach((d) => { const u = d?.url || d?.fileUrl || d?.downloadUrl; if (u) urls.push({ url: u, name: d?.fileName || d?.title || d?.name || 'document' }); });
      const subDocs = ds?.scrutinySubmission?.documents;
      if (Array.isArray(subDocs)) {
        subDocs.forEach((d) => { const u = d?.url || d?.fileUrl || d?.downloadUrl; if (u) urls.push({ url: u, name: d?.fileName || d?.title || d?.name || 'document' }); });
      }
      // 3) Fallback to application.documents object
      const appDocs = application?.documents;
      if (appDocs && typeof appDocs === 'object') {
        Object.values(appDocs).forEach((d) => { const u = d?.url || d?.fileUrl || d?.downloadUrl; if (u) urls.push({ url: u, name: d?.fileName || d?.title || d?.name || 'document' }); });
      }
    } catch (_) {}
    return urls;
  };

  const triggerBrowserDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (_) {
      window.open(url, '_blank');
    }
  };

  const handleDownloadAllScrutinyDocs = async () => {
    const files = collectScrutinyDocUrls();
    if (!files.length) return;
    files.forEach(f => triggerBrowserDownload(f.url, f.name));
  };

  const loadJsPdf = async () => {
    if (window.jspdf && window.jspdf.jsPDF) return window.jspdf.jsPDF;
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });
    return window.jspdf.jsPDF;
  };

  const fetchAsDataUrl = async (url) => {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve({ dataUrl: r.result, type: blob.type });
      r.readAsDataURL(blob);
    });
  };

  const handleDownloadImagesAsSinglePdf = async () => {
    try {
      const all = collectScrutinyDocUrls();
      if (!all.length) return;
      const imageCandidates = all.filter(f => /\.(png|jpg|jpeg|webp|gif)$/i.test(f.name) || /image\//.test(f?.type || ''));
      if (imageCandidates.length === 0) return;

      const jsPDFCtor = await loadJsPdf();
      const pdf = new jsPDFCtor({ unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      let first = true;
      for (const f of imageCandidates) {
        const { dataUrl } = await fetchAsDataUrl(f.url);
        const img = new Image();
        img.src = dataUrl;
        await new Promise(res => { img.onload = res; img.onerror = res; });
        const imgW = img.width;
        const imgH = img.height;
        if (!first) pdf.addPage();
        first = false;
        // Fit preserving aspect
        const ratio = Math.min(pageWidth / imgW, pageHeight / imgH);
        const drawW = imgW * ratio;
        const drawH = imgH * ratio;
        const x = (pageWidth - drawW) / 2;
        const y = (pageHeight - drawH) / 2;
        pdf.addImage(dataUrl, 'JPEG', x, y, drawW, drawH, undefined, 'FAST');
      }

      const filename = `${application?.applicationId || 'application'}_scrutiny_images.pdf`;
      pdf.save(filename);
    } catch (e) {
      console.error('Failed to create combined PDF', e);
      alert('Could not generate combined PDF. Please try again.');
    }
  };

  const loadPdfLib = async () => {
    if (window.PDFLib && window.PDFLib.PDFDocument) return window.PDFLib;
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/dist/pdf-lib.min.js';
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });
    return window.PDFLib;
  };

  const fetchAsArrayBuffer = async (url) => {
    const resp = await fetch(url);
    return await resp.arrayBuffer();
  };

  const handleDownloadAllDocsAsSinglePdf = async () => {
    try {
      const files = collectScrutinyDocUrls();
      if (!files.length) return;
      const { PDFDocument, StandardFonts } = await loadPdfLib();
      const merged = await PDFDocument.create();
      const a4Width = 595.28; // pt
      const a4Height = 841.89;

      for (const f of files) {
        const name = (f.name || '').toLowerCase();
        const isPdf = name.endsWith('.pdf');
        if (isPdf) {
          try {
            const srcBytes = await fetchAsArrayBuffer(f.url);
            const srcPdf = await PDFDocument.load(srcBytes);
            const pages = await merged.copyPages(srcPdf, srcPdf.getPageIndices());
            pages.forEach(p => merged.addPage(p));
            continue;
          } catch (_) {
            // fall through to image embed attempt
          }
        }

        // Treat as image (png/jpg/webp/gif)
        try {
          const { dataUrl } = await fetchAsDataUrl(f.url);
          const imgBytes = await (await fetch(dataUrl)).arrayBuffer();
          let img;
          if (name.endsWith('.png')) img = await merged.embedPng(imgBytes);
          else img = await merged.embedJpg(imgBytes); // jpg/jpeg/webp/gif -> jpg path
          const imgWidth = img.width;
          const imgHeight = img.height;
          const page = merged.addPage([a4Width, a4Height]);
          const ratio = Math.min(a4Width / imgWidth, a4Height / imgHeight);
          const drawW = imgWidth * ratio;
          const drawH = imgHeight * ratio;
          const x = (a4Width - drawW) / 2;
          const y = (a4Height - drawH) / 2;
          page.drawImage(img, { x, y, width: drawW, height: drawH });
        } catch (e) {
          // As a last resort, add a placeholder page noting an unsupported file
          const page = merged.addPage([a4Width, a4Height]);
          const font = await merged.embedFont(StandardFonts.Helvetica);
          page.drawText('Unsupported file for merge: ' + (f.name || 'document'), {
            x: 40, y: a4Height - 80, size: 12, font
          });
          page.drawText(f.url, { x: 40, y: a4Height - 100, size: 10, font });
        }
      }

      const mergedBytes = await merged.save();
      const blob = new Blob([mergedBytes], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      triggerBrowserDownload(blobUrl, `${application?.applicationId || 'application'}_all_documents.pdf`);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Failed to merge all documents', e);
      alert('Could not merge all documents into one PDF.');
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const Script = ({ type, application }) => {
    const reviewerName = (user?.displayName || user?.name || (user?.email ? user.email.split('@')[0] : '') || '').trim();
    const appWithReviewer = reviewerName ? { ...application, reviewerName } : application;
    const script = getConsultationScripts(type, appWithReviewer);
    
    if (!script) return <div>No script available</div>;

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{script.title}</h3>
          <button
            onClick={() => setShowScript(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Introduction</h4>
            <p className="text-blue-800 text-sm leading-relaxed">{script.introduction}</p>
          </div>

          {script.sections.map((section, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">{section.title}</h4>
              <ul className="space-y-2">
                {section.points.map((point, pointIndex) => (
                  <li key={pointIndex} className="text-sm text-gray-700 flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-2">Conclusion</h4>
            <p className="text-green-800 text-sm leading-relaxed">{script.conclusion}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Phone className="h-5 w-5 mr-2 text-blue-600" />
              Patient Consultations
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Schedule and manage pre/post consultation calls with patients
            </p>
            {/* Summary chips inspired by medical review */}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {isKypAccepted() && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  KYP Accepted
                </span>
              )}
              {isDispatchApproved() && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Dispatch Approved
                </span>
              )}
              {(() => {
                const s = getApprovedDocsSummary();
                if (!s) return null;
                return (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
                    Approved Docs: {s.approved} / {s.total}
                  </span>
                );
              })()}
            </div>
          </div>
          { (isKypAccepted() || isDispatchApproved()) && (
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setScriptType('kyp-initial-counselling');
                  setShowScript(true);
                }}
                className="px-4 py-2 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 flex items-center text-sm"
              >
                <FileText className="h-4 w-4 mr-2" />
                View Script
              </button>
              <button
                onClick={() => setShowScheduleForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center text-sm"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Schedule Consultation
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8 px-6">
          {['schedule', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
              {tab === 'history' && consultations.length > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                  {consultations.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Scrutiny Document Review snapshot - only show when dispatch is approved */}
        {isDispatchApproved() && (
          <div className="mb-6 border border-teal-200 bg-teal-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-teal-900">Scrutiny Document Review</h3>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white text-emerald-700 border border-emerald-200">
                  Dispatch Process Approved
                </span>
                {collectScrutinyDocUrls().length > 0 && (
                  <button
                    onClick={handleDownloadAllDocsAsSinglePdf}
                    className="inline-flex items-center px-2.5 py-1 rounded text-xs bg-teal-600 text-white hover:bg-teal-700"
                    title="Download all scrutiny documents as a single PDF"
                  >
                    <FileText className="h-3 w-3 mr-1" /> Download All as PDF
                  </button>
                )}
              </div>
            </div>
            {(() => {
              const s = getApprovedDocsSummary();
              return (
                <p className="text-sm text-teal-800 mb-3">Approved Documents: {s ? `${s.approved} of ${s.total}` : 'No scrutiny checklist yet'}</p>
              );
            })()}
            {Array.isArray(getScrutinyChecklist()) && getScrutinyChecklist().length > 0 ? (
              <div className="bg-white rounded border border-teal-100">
                <ul className="divide-y divide-gray-100">
                  {getScrutinyChecklist().slice(0, 5).map((item, idx) => (
                    <li key={idx} className="px-3 py-2 text-sm flex items-center justify-between gap-3">
                      <div className="truncate">
                        <span className="font-medium text-gray-900 truncate">{item?.title || item?.name || item?.fileName || 'Document'}</span>
                        <span className="ml-2 text-xs text-gray-500">Status: {(item?.status || '').toString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full border ${String(item?.status || '').toLowerCase() === 'approved' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                          {(item?.status || '').toString().replace(/_/g,' ')}
                        </span>
                        {(item?.url || item?.fileUrl || item?.downloadUrl) && (
                          <button
                            onClick={() => triggerBrowserDownload(item?.url || item?.fileUrl || item?.downloadUrl, item?.fileName || item?.title || item?.name || 'document')}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs border border-gray-200 hover:bg-gray-50"
                            title="Download"
                          >
                            <Download className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-xs text-teal-800">Once scrutiny starts, a quick summary appears here.</div>
            )}
            <p className="text-xs text-teal-800 mt-2">Open the Documents tab to view all files and the full scrutiny list.</p>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="space-y-6">
            {/* Current Status */}
          {(isKypAccepted() || isDispatchApproved()) ? (
            <div className="grid grid-cols-2 gap-4">
              {isKypAccepted() && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-medium text-blue-900 mb-2">KYP Initial Counselling</h3>
                  <div className="text-sm text-blue-700">
                    Status: {application?.workflow?.policyVerification?.kypInitialCounselling?.status || 'Available to Schedule'}
                  </div>
                </div>
              )}
              {isDispatchApproved() && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-medium text-green-900 mb-2">Discharge Counselling</h3>
                  <div className="text-sm text-green-700">
                    Status: {application?.workflow?.policyVerification?.dischargeCounselling?.status || 'Available to Schedule'}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-600">
              <Calendar className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <div className="font-medium">No counselling available yet</div>
              <div className="text-sm mt-1">Counselling appears after KYP acceptance or dispatch approval.</div>
            </div>
          )}

            {/* Upcoming Consultations */}
            <div>
              <h3 className="font-medium text-gray-900 mb-4">Upcoming Consultations</h3>
              {consultations.filter(c => c.status === 'scheduled' && isTypeAllowed(c.consultationType)).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No upcoming consultations scheduled</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {consultations.filter(c => c.status === 'scheduled' && isTypeAllowed(c.consultationType)).map((consultation) => (
                    <div key={consultation.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              consultation.consultationType === 'kyp-initial-counselling' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {consultation.consultationType}
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatDateTime(consultation.scheduledDate)}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            {consultation.contactMethod === 'phone' && <Phone className="h-4 w-4 mr-1" />}
                            {consultation.contactMethod === 'video' && <Video className="h-4 w-4 mr-1" />}
                            {consultation.contactMethod === 'in-person' && <MapPin className="h-4 w-4 mr-1" />}
                            {consultation.contactMethod} - {consultation.patientPhone}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedConsultation(consultation);
                            setShowCompleteForm(true);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                        >
                          Complete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            {consultations.filter(c => isTypeAllowed(c.consultationType)).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No consultation history available</p>
              </div>
            ) : (
              consultations.filter(c => isTypeAllowed(c.consultationType)).map((consultation) => (
                <div key={consultation.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        consultation.consultationType === 'kyp-initial-counselling' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {consultation.consultationType}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getConsultationStatusColor(consultation.status)}`}>
                        {consultation.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDateTime(consultation.completedAt || consultation.scheduledDate)}
                    </div>
                  </div>

                  {consultation.status === 'completed' && (
                    <div className="space-y-2 text-sm">
                      <div><strong>Duration:</strong> {consultation.duration} minutes</div>
                      {consultation.consultationNotes && (
                        <div><strong>Notes:</strong> {consultation.consultationNotes}</div>
                      )}
                      {consultation.patientSatisfaction && (
                        <div className="flex items-center">
                          <strong className="mr-2">Satisfaction:</strong>
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-4 w-4 ${
                                  star <= consultation.patientSatisfaction
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {consultation.audioRecordingUrl && (
                        <div>
                          <a
                            href={consultation.audioRecordingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            <Mic className="h-4 w-4 mr-1" />
                            Listen to Recording
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Schedule Consultation Modal */}
      {showScheduleForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Schedule Consultation</h3>
              <button
                onClick={() => setShowScheduleForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Consultation Type
                  </label>
                  <select
                    value={scheduleData.type}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {getAvailableConsultationTypes().map((t) => (
                      <option key={t} value={t}>
                        {t === 'kyp-initial-counselling' ? 'KYP Initial Counselling' : 'Discharge Counselling'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Method
                  </label>
                  <select
                    value={scheduleData.contactMethod}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, contactMethod: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="phone">Phone Call</option>
                    <option value="video">Video Call</option>
                    <option value="in-person">In-Person</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={scheduleData.scheduledDate}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time
                  </label>
                  <input
                    type="time"
                    value={scheduleData.scheduledTime}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, scheduledTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patient Phone
                  </label>
                  <input
                    type="tel"
                    value={scheduleData.patientPhone}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, patientPhone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={scheduleData.priority}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={scheduleData.notes}
                  onChange={(e) => setScheduleData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any specific notes or agenda items..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowScheduleForm(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleConsultation}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Scheduling...' : 'Schedule Consultation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Consultation Modal */}
      {showCompleteForm && selectedConsultation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Complete {selectedConsultation.consultationType}
              </h3>
              <button
                onClick={() => {
                  setShowCompleteForm(false);
                  setSelectedConsultation(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={completionData.duration}
                    onChange={(e) => setCompletionData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patient Satisfaction (1-5)
                  </label>
                  <select
                    value={completionData.patientSatisfaction}
                    onChange={(e) => setCompletionData(prev => ({ ...prev, patientSatisfaction: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[1, 2, 3, 4, 5].map(num => (
                      <option key={num} value={num}>{num} Star{num > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Topics Discussed
                </label>
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newTopic}
                      onChange={(e) => setNewTopic(e.target.value)}
                      placeholder="Add topic discussed..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addToList('topicsDiscussed', newTopic, setCompletionData);
                          setNewTopic('');
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        addToList('topicsDiscussed', newTopic, setCompletionData);
                        setNewTopic('');
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  {completionData.topicsDiscussed.length > 0 && (
                    <div className="space-y-1">
                      {completionData.topicsDiscussed.map((topic, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                          <span className="text-sm">{topic}</span>
                          <button
                            onClick={() => removeFromList('topicsDiscussed', index, setCompletionData)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Consultation Notes *
                </label>
                <textarea
                  value={completionData.consultationNotes}
                  onChange={(e) => setCompletionData(prev => ({ ...prev, consultationNotes: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Detailed notes about the consultation..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient Feedback
                </label>
                <textarea
                  value={completionData.patientFeedback}
                  onChange={(e) => setCompletionData(prev => ({ ...prev, patientFeedback: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="What did the patient say or express?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Action Items
                </label>
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newActionItem}
                      onChange={(e) => setNewActionItem(e.target.value)}
                      placeholder="Add action item..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addToList('actionItems', newActionItem, setCompletionData);
                          setNewActionItem('');
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        addToList('actionItems', newActionItem, setCompletionData);
                        setNewActionItem('');
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  {completionData.actionItems.length > 0 && (
                    <div className="space-y-1">
                      {completionData.actionItems.map((item, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                          <span className="text-sm">{item}</span>
                          <button
                            onClick={() => removeFromList('actionItems', index, setCompletionData)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audio Recording
                </label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setCompletionData(prev => ({ ...prev, audioFile: e.target.files[0] }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Upload audio recording of the consultation (optional)</p>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="followUpRequired"
                  checked={completionData.followUpRequired}
                  onChange={(e) => setCompletionData(prev => ({ ...prev, followUpRequired: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="followUpRequired" className="text-sm font-medium text-gray-700">
                  Follow-up required
                </label>
                {completionData.followUpRequired && (
                  <input
                    type="date"
                    value={completionData.followUpDate}
                    onChange={(e) => setCompletionData(prev => ({ ...prev, followUpDate: e.target.value }))}
                    className="px-3 py-1 border border-gray-300 rounded text-sm"
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowCompleteForm(false);
                  setSelectedConsultation(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteConsultation}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Completing...' : 'Complete Consultation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Script Modal */}
      {showScript && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">Consultation Script</h3>
                <select
                  value={scriptType}
                  onChange={(e) => setScriptType(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="kyp-initial-counselling">KYP Initial Counselling</option>
                  <option 
                    value="discharge-counselling"
                    disabled={!application?.dischargeInitiated && application?.status !== 'discharge_process'}
                  >
                    Discharge Counselling
                  </option>
                </select>
              </div>
              <button
                onClick={() => setShowScript(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <Script type={scriptType} application={application} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationScheduler;
